import React from "react";
import { Popover, Button, Tooltip, Divider, Dropdown, Menu } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  EditOutlined,
  LinkOutlined,
  DisconnectOutlined,
  FontSizeOutlined,
  FontColorsOutlined,
  DownOutlined,
  UnorderedListOutlined as ListIcon,
  FormatPainterOutlined,
} from "@ant-design/icons";

import "../../styles/bulletinAdmin/components/bulletinBlock.scss";

export default function PopoverToolbar({
  toolbarRef,
  showPopover,
  children,
  // Rich text props
  execCommand,
  activeFormats = {},
  // Align props
  onAlignChange,
  currentAlign,
  // Upload props
  onUploadClick,
  // Link props
  onLinkClick,
  // Font style props
  onFontSizeChange,
  currentFontSize = "body",
  onFontColorChange,
  currentFontColor = "#000000",
  // Control props
  showRichTextControls = false,
  showAlignControls = true,
  showUploadControls = false,
  // Popover props
  onOpenChange,
}) {
  // Dropdown Menus for various controls
  const richTextMenu = (
    <Menu>
      <Menu.Item
        key="bold"
        onClick={() => execCommand("bold")}
        icon={<BoldOutlined />}
      >
        Bold
      </Menu.Item>
      <Menu.Item
        key="italic"
        onClick={() => execCommand("italic")}
        icon={<ItalicOutlined />}
      >
        Italic
      </Menu.Item>
      <Menu.Item
        key="underline"
        onClick={() => execCommand("underline")}
        icon={<UnderlineOutlined />}
      >
        Underline
      </Menu.Item>
    </Menu>
  );

  const listMenu = (
    <Menu>
      <Menu.Item
        key="ul"
        onClick={() => execCommand("insertUnorderedList")}
        icon={<UnorderedListOutlined />}
      >
        Bulleted List
      </Menu.Item>
      <Menu.Item
        key="ol"
        onClick={() => execCommand("insertOrderedList")}
        icon={<OrderedListOutlined />}
      >
        Numbered List
      </Menu.Item>
    </Menu>
  );

  const fontSizeMenu = (
    <Menu
      onClick={({ key }) => onFontSizeChange(key)}
      selectedKeys={[currentFontSize]}
    >
      <Menu.Item key="heading">Heading</Menu.Item>
      <Menu.Item key="subheading">Subheading</Menu.Item>
      <Menu.Item key="body">Body</Menu.Item>
    </Menu>
  );

  const fontColorMenu = (
    <Menu
      onClick={({ key }) => onFontColorChange(key)}
      selectedKeys={[currentFontColor]}
    >
      <Menu.Item key="#000000">
        <span
          style={{
            display: "inline-block",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: "#000000",
            marginRight: "8px",
            verticalAlign: "middle",
            border: "1px solid #d9d9d9",
          }}
        />
        Black
      </Menu.Item>
      <Menu.Item key="#862633">
        <span
          style={{
            display: "inline-block",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: "#862633",
            marginRight: "8px",
            verticalAlign: "middle",
          }}
        />
        Maroon
      </Menu.Item>
      <Menu.Item key="#006644">
        <span
          style={{
            display: "inline-block",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: "#006644",
            marginRight: "8px",
            verticalAlign: "middle",
          }}
        />
        Green
      </Menu.Item>
    </Menu>
  );

  // Toolbar content with rich text, alignment, and upload controls
  const richTextContent = showRichTextControls && execCommand && (
    <>
      <Tooltip title="Text Type">
        <Dropdown overlay={fontSizeMenu}>
          <Button
            type="text"
            className="format-btn"
            icon={<FontSizeOutlined />}
          />
        </Dropdown>
      </Tooltip>
      <Tooltip title="Text Formatting">
        <Dropdown overlay={richTextMenu}>
          <Button
            type="text"
            className="format-btn"
            icon={<FormatPainterOutlined />}
          />
        </Dropdown>
      </Tooltip>
      <Tooltip title="Font Color">
        <Dropdown overlay={fontColorMenu}>
          <Button type="text" className="format-btn">
            <span
              style={{
                display: "inline-block",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: currentFontColor,
                border: "1px solid #ccc",
                verticalAlign: "middle",
              }}
            />
          </Button>
        </Dropdown>
      </Tooltip>
      <Tooltip title="List Style">
        <Dropdown overlay={listMenu}>
          <Button type="text" className="format-btn" icon={<ListIcon />} />
        </Dropdown>
      </Tooltip>
      {activeFormats.isLink ? (
        <Tooltip title="Unlink">
          <Button
            type="text"
            className={"format-btn active"}
            onClick={() => execCommand("unlink")}
            icon={<DisconnectOutlined />}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Link">
          <Button
            type="text"
            className={"format-btn"}
            onClick={onLinkClick}
            icon={<LinkOutlined />}
          />
        </Tooltip>
      )}
    </>
  );

  const alignContent = showAlignControls && onAlignChange && (
    <>
      <Tooltip title="Left Align">
        <Button
          type="text"
          className={
            (currentAlign || "left") === "left"
              ? "format-btn active"
              : "format-btn"
          }
          onClick={() => onAlignChange("left")}
          icon={<AlignLeftOutlined />}
        />
      </Tooltip>
      <Tooltip title="Center Align">
        <Button
          type="text"
          className={
            currentAlign === "center" ? "format-btn active" : "format-btn"
          }
          onClick={() => onAlignChange("center")}
          icon={<AlignCenterOutlined />}
        />
      </Tooltip>
      <Tooltip title="Right Align">
        <Button
          type="text"
          className={
            currentAlign === "right" ? "format-btn active" : "format-btn"
          }
          onClick={() => onAlignChange("right")}
          icon={<AlignRightOutlined />}
        />
      </Tooltip>
    </>
  );

  const uploadContent = showUploadControls && onUploadClick && (
    <Tooltip title="Replace">
      <Button
        type="text"
        className="format-btn"
        onClick={onUploadClick}
        icon={<EditOutlined />}
      />
    </Tooltip>
  );

  const controls = [richTextContent, alignContent, uploadContent].filter(
    Boolean
  );

  if (controls.length === 0) {
    return <>{children}</>;
  }

  const content = (
    <div ref={toolbarRef} style={{ display: "flex", alignItems: "center" }}>
      {controls.map((control, index) => (
        <React.Fragment key={index}>
          {control}
          {index < controls.length - 1 && <Divider type="vertical" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <Popover
      content={content}
      open={showPopover}
      trigger="hover"
      placement="topLeft"
      onOpenChange={onOpenChange}
    >
      {children}
    </Popover>
  );
}
